import React, { useMemo } from 'react';
import { Link } from 'react-router';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { renderImage, SEARCH_EPISODE_TILE_IMAGE } from 'helpers/ImageHelper';
import Badge from 'componentsV2/Badge';
import { SITE_PLATFORM_MOBILE } from 'helpers/BreakpointHelper';
import { Detail, Overlay, PlayButton } from '../../components';
import Style from '../../Styles/EpisodeTile.module.scss';

const EpisodeTile = ({
  theme = {},
  breakpoint,
  processor,
  item,
  account,
  searchProps,
  itemIndex,
}) => {
  const {
    title,
    image,
    altText,
    description,
    subtitle,
    airedDate,
    link,
    badgeProps,
    onClick,
  } = processor({ item, account, searchProps });
  const isMobile = breakpoint === SITE_PLATFORM_MOBILE;

  const episodeDetail = useMemo(
    () => (
      <Detail Style={{ details: classNames(Style.details, theme.details) }}>
        <div className={classNames(Style.title, theme.title)}>{title}</div>
        <div className={classNames(Style.subtitleContainer, theme.subtitleContainer)}>
          <div className={classNames(Style.subtitle, theme.subtitle)}>{subtitle}</div>
          {airedDate && (
            <div className={classNames(Style.subtitleInnerContainer, theme.subtitleInnerContainer)}>
              <div className={classNames(Style.dot, theme.dot)} />
              <div className={classNames(Style.subtitle, theme.subtitle)}>{airedDate}</div>
            </div>
          )}
        </div>
        <div className={classNames(Style.description, theme.description)}>{description}</div>
      </Detail>
    ),
    [airedDate, description, subtitle, theme, title],
  );

  const episodeImg = useMemo(
    () => (
      <>
        {renderImage({
          breakpoint,
          imageType: SEARCH_EPISODE_TILE_IMAGE,
          alt: altText,
          src: image,
        })}
        <Badge {...badgeProps} theme={Style} />
      </>
    ),
    [altText, badgeProps, breakpoint, image],
  );

  const episode = useMemo(
    () => (
      <div className={classNames(Style.tile, theme.tile)} key={itemIndex}>
        <div className={classNames(Style.imageContainer, theme.imageContainer)}>
          <Overlay Style={Style}>
            <PlayButton
              ariaLabel={`${title} episode`}
              link={link}
              playButtonClick={onClick}
              render
            />
          </Overlay>
          {episodeImg}
        </div>
        {episodeDetail}
      </div>
    ),
    [episodeDetail, episodeImg, itemIndex, link, onClick, theme.imageContainer, theme.tile, title],
  );

  const episodeMobile = useMemo(
    () => (
      <Link
        aria-label={`${title} episode`}
        className={classNames(Style.tile, theme.tile)}
        key={itemIndex}
        onClick={onClick}
        to={link}
      >
        {episodeImg}
        {episodeDetail}
      </Link>
    ),
    [episodeDetail, episodeImg, itemIndex, link, onClick, theme.tile, title],
  );

  if (isMobile) return episodeMobile;

  return episode;
};

EpisodeTile.propTypes = {
  account: PropTypes.shape({}),
  breakpoint: PropTypes.string,
  item: PropTypes.shape({}),
  itemIndex: PropTypes.number,
  processor: PropTypes.func,
  searchProps: PropTypes.shape({}),
  theme: PropTypes.shape({}),
};

export default EpisodeTile;
